import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { Box, ThemeOptions } from '@material-ui/core';

import { ArchivistProfileContext } from 'core/context/admin/archivist-profile';
import { AppRoutes } from 'core/helpers';
import { ArchivistsApi } from 'core/api/admin';

import { BackNavigation, Loader } from 'components/_others';

import { Lightbox } from './components/Lightbox';
import { Stats } from './components/Stats';
import { Bio } from './components/Bio';
import { UserRoles } from '../../../../../core/enums';
import { ChangeUserRole } from '../../../../../components/_dashboard/_user-profiles/ChangeUserRole';
import { UtilsContext } from '../../../../../core/context/utils.context';
import { AssignJobBtn } from '../../../../../components/jobs/AssignJobBtn';
import { AssignJobModal } from '../../../../../components/jobs/AssignJobModal';
import { ActiveJobsList } from '../../../../../components/jobs/ActiveJobsList';

const useStyles = makeStyles((theme: ThemeOptions) => ({
    root: {
        border: '1px solid',
        borderColor: theme.divider.main,
        marginTop: 20,
        display: 'flex',
        justifyContent: 'space-between',
        padding: '20px',
    },
    profileBox: {
        width: '46%',
    },
    artifactsBox: {
        width: '49%',
    },
    divider: {
        width: '40%',
        borderTop: '1px solid',
        borderColor: theme.divider.main,
        margin: '20px 0',
    },
    jobsBox: {
        width: '49%',
        borderLeft: '1px solid',
        borderColor: theme.divider.main,
        paddingLeft: 15,
    },
}));

export const ArchivistProfile: React.FC = () => {
    const classes = useStyles();
    const history = useHistory();
    const archivistProfileContext = useContext(ArchivistProfileContext);

    const [isLoading, setIsLoading] = useState(true);
    const [refreshJobs, setRefreshJobs] = useState(0);
    const [totalJobs, setTotalJobs] = useState({ totalJobs: 0, totalActiveJobs: 0 });
    const [displayJobsModal, toggleJobsModal] = useState(false);

    const utilsContext = useContext(UtilsContext);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const id = params.get('id');

        if (!id) {
            history.push(AppRoutes.ARCHIVIST_LISTING);
        }

        ArchivistsApi.getArchivistProfile(Number(id))
            .then((res: any) => {
                archivistProfileContext.setData(res.data);
                setTotalJobs({ totalJobs: res.data.totalJobs, totalActiveJobs: res.data.totalActiveJobs });
                window.scrollTo(0, 0);
                setIsLoading(false);
            })
            .catch((error: any) => {
                console.error(error.response);
                history.push(AppRoutes.ARCHIVIST_LISTING);
                utilsContext.addSnackbar('error');
            });
    }, []);

    return (
        <Box>
            {!isLoading && (
                <>
                    <BackNavigation />

                    <Box className={classes.root}>
                        <Box className={classes.profileBox}>
                            <Box>
                                <Lightbox
                                    user={archivistProfileContext.data}
                                    hasActiveJobs={totalJobs.totalActiveJobs > 0}
                                />

                                <div className={classes.divider} />

                                <ChangeUserRole currentRole={UserRoles.ARCHIVIST} user={archivistProfileContext.data} />

                                <div className={classes.divider} />

                                <Bio user={archivistProfileContext.data} />
                            </Box>
                        </Box>

                        <Box className={classes.jobsBox}>
                            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                                <Stats totalJobs={totalJobs.totalJobs} totalActiveJobs={totalJobs.totalActiveJobs} />

                                {archivistProfileContext.data.isActive && (
                                    <AssignJobBtn text={'Assign Collector'} onClick={() => toggleJobsModal(true)} />
                                )}

                                {displayJobsModal && (
                                    <AssignJobModal
                                        open={true}
                                        handleClose={() => {
                                            toggleJobsModal(false);
                                            setRefreshJobs(refreshJobs + 1);
                                        }}
                                        title={'Assign Collector'}
                                        jobRole={UserRoles.COLLECTOR}
                                        userId={archivistProfileContext.data.userId}
                                        userRole={UserRoles.ARCHIVIST}
                                    />
                                )}
                            </Box>

                            {archivistProfileContext.data.isActive && (
                                <>
                                    <div className={classes.divider} />
                                    <ActiveJobsList
                                        userId={archivistProfileContext.data.userId}
                                        role={UserRoles.ARCHIVIST}
                                        refresh={refreshJobs}
                                        hasHistory={totalJobs.totalJobs > totalJobs.totalActiveJobs}
                                        onJobStatistics={(jobs: any) => setTotalJobs(jobs)}
                                    />
                                </>
                            )}
                        </Box>
                    </Box>
                </>
            )}

            {isLoading && <Loader />}
        </Box>
    );
};
